import IonIcon from '@reacticons/ionicons'
import { useRouter } from 'next/router'
import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { AddCustomTagModal } from 'src/components/CustomTag/AddCustomTagModal'
import { DeleteTagModal } from 'src/components/CustomTag/DeleteTagModal'
import { EditTagModal } from 'src/components/CustomTag/EditTagModal'
import { CheckBox } from 'src/components/global/CheckBox/CheckBox'
import { INITIAL_ITEM_CNT } from 'src/config/config'
import { ICustomTagResult, useGetMyCustomTagsQuery } from 'src/graphql/manual/portfolioCustomTagHooks'
import { useUpdateQueryParams } from 'src/hooks/useUpdateQueryParams'

export const CUSTOM_TAG_QUERY_PARAM = 'customTags'

interface Props {
  opened: boolean
  onSelectAccordion: () => void
  onTagsChanged: () => void | Promise<void>
  sharedPortfolioId?: string
  isSharedPortfolio?: boolean
}

interface MenuPosition {
  top: number
  right: number
}

export const CustomTagFilter: React.FC<Props> = ({
  opened,
  onSelectAccordion,
  onTagsChanged,
  sharedPortfolioId = '',
  isSharedPortfolio = true,
}) => {
  const router = useRouter()
  const { updateQueryParams } = useUpdateQueryParams()
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null)
  const [menuPos, setMenuPos] = useState<MenuPosition | null>(null)
  const [editModalTag, setEditModalTag] = useState<ICustomTagResult | null>(null)
  const [deleteModalTag, setDeleteModalTag] = useState<ICustomTagResult | null>(null)
  const [showAll, setShowAll] = useState(false)
  const [showCreateTagModal, setShowCreateTagModal] = useState(false)

  const { data, refetch } = useGetMyCustomTagsQuery({
    variables: {
      portfolioId: sharedPortfolioId,
      isSharedPortfolio,
    },
    fetchPolicy: 'network-only',
    skip: !sharedPortfolioId,
  })

  const tags = (data?.getMyCustomTags || []).filter(Boolean) as ICustomTagResult[]

  const selectedIds: Set<string> = new Set(
    router.query[CUSTOM_TAG_QUERY_PARAM]
      ? (router.query[CUSTOM_TAG_QUERY_PARAM] as string).split(',').filter(Boolean)
      : [],
  )

  useEffect(() => {
    const handleClickOutside = () => setActiveMenuId(null)
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const onChanged = (checked: boolean, tagId?: string) => {
    if (!tagId) return
    const next = new Set(selectedIds)
    if (checked) next.add(tagId)
    else next.delete(tagId)
    const value = [...next].join(',')
    updateQueryParams({ [CUSTOM_TAG_QUERY_PARAM]: value || undefined, page: undefined })
  }

  const handleDeleteSuccess = (tagId: string) => {
    const next = new Set(selectedIds)
    next.delete(tagId)
    const value = [...next].join(',')
    updateQueryParams({ [CUSTOM_TAG_QUERY_PARAM]: value || undefined, page: undefined })
    void refetch()
    onTagsChanged()
  }

  const handleCreateSuccess = async () => {
    await refetch()
    await onTagsChanged()
  }

  const handleEditSuccess = async () => {
    const next = new Set(selectedIds)
    const value = [...next].join(',')
    updateQueryParams({ [CUSTOM_TAG_QUERY_PARAM]: value || undefined, page: undefined })
    void refetch()
    onTagsChanged()
  }

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>, tagId: string) => {
    e.stopPropagation()
    if (activeMenuId === tagId) {
      setActiveMenuId(null)
      return
    }
    const rect = e.currentTarget.getBoundingClientRect()
    setMenuPos({
      top: rect.bottom + window.scrollY + 4,
      right: window.innerWidth - rect.right,
    })
    setActiveMenuId(tagId)
  }

  const activeTag = tags.find((t) => t.id === activeMenuId) ?? null

  return (
    <>
      <div className="w-full flex flex-col">
        <button
          type="button"
          className="py-3 box-border appearance-none cursor-pointer focus:outline-none flex items-center justify-between"
          onClick={onSelectAccordion}
          aria-expanded={opened}
        >
          <span className="inline-flex items-center gap-2.5 min-w-0">
            <p className="inline-block text-footnote light font-bold font-primary text-textdark uppercase text-[0.94rem]">
              Custom Tags
            </p>
            <span
              role="button"
              tabIndex={0}
              title="Create custom tag"
              aria-label="Create custom tag"
              className="inline-flex h-[16px] w-[16px] shrink-0 items-center justify-center rounded-full bg-[#011D58] hover:opacity-80 focus:outline-none"
              onClick={(e) => {
                e.stopPropagation()
                setShowCreateTagModal(true)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  setShowCreateTagModal(true)
                }
              }}
            >
              {/* The svg spans the whole circle so the bars share its pixel snapping and cannot drift off
                  centre at fractional browser zoom. Even-width bars on integer coordinates stay crisp. */}
              {/* <svg viewBox="0 0 16 16" aria-hidden="true" className="block h-full w-full">
                <rect x="4" y="7" width="8" height="2" fill="white" />
                <rect x="7" y="4" width="2" height="8" fill="white" />
              </svg> */}
              <svg className="block h-[16px] w-[16px]" viewBox="0 0 16 16" fill="none">
                <circle cx="8" cy="8" r="7.25" fill="#011D58" />
                <rect x="7" y="4" width="2" height="8" rx="1" fill="#fff" />
                <rect x="4" y="7" width="8" height="2" rx="1" fill="#fff" />
              </svg>
            </span>
          </span>
          {opened ? <IonIcon name="chevron-up-outline" /> : <IonIcon name="chevron-down-outline" />}
        </button>

        <div
          style={{ maxHeight: opened ? '380px' : '0px' }}
          className="transition-max-height duration-500 overflow-hidden overflow-y-scroll ease-in-out"
        >
          {!tags.length && (
            <p className="pb-4 mr-1 font-primary text-[#767676] text-[0.94rem] mt-[12px] mb-[12px]">
              No custom tags yet. Click + to create one.
            </p>
          )}
          {tags.map((tag, index) => {
            if (index >= INITIAL_ITEM_CNT && !showAll) return <div key={tag.id} />

            let clsName = ''
            if (index === 0) {
              clsName = 'pb-4 pl-[1.25rem] mr-1 font-bold font-primary text-textdark text-[0.94rem] mt-[12px]'
            } else if (index === tags.length - 1) {
              clsName = 'pb-4 pl-[1.25rem] mr-1 font-bold font-primary text-textdark text-[0.94rem] mb-[12px]'
            } else {
              clsName = 'pb-4 pl-[1.25rem] mr-1 font-bold font-primary text-textdark text-[0.94rem]'
            }

            return (
              <div key={tag.id} className={clsName}>
                <div className="flex items-start justify-between gap-1">
                  <CheckBox
                    label={tag.name}
                    value={tag.id}
                    onChanged={onChanged}
                    selected={selectedIds.has(tag.id)}
                    indeterminate={selectedIds.has(tag.id)}
                    accordionOpened={opened}
                    className="min-w-0 flex-1"
                    labelClassName="min-w-0 flex-1 break-words font-primary font-medium text-[15px] text-darkgray text-left cursor-pointer"
                  />
                  <button
                    type="button"
                    onClick={(e) => openMenu(e, tag.id)}
                    className="mr-1 px-1 shrink-0 text-[16px] leading-5 text-[#767676] hover:text-[#011D58]"
                    title="Tag options"
                  >
                    ⋮
                  </button>
                </div>
              </div>
            )
          })}
          {tags.length > INITIAL_ITEM_CNT && !showAll && (
            <button
              className="ml-[1.22rem] px-2 py-0 mb-[1rem] font-primary text-textdark text-[0.94rem] underline cursor-pointer"
              onClick={() => setShowAll(true)}
            >
              +{tags.length - INITIAL_ITEM_CNT} more
            </button>
          )}
        </div>
      </div>

      {activeMenuId &&
        menuPos &&
        activeTag &&
        createPortal(
          <div
            style={{ position: 'absolute', top: menuPos.top, right: menuPos.right, zIndex: 9999 }}
            className="w-[100px] rounded-[6px] border border-[#DDDDDD] bg-white shadow-md"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="w-full px-3 py-2 text-left text-[13px] text-[#011D58] hover:bg-[#F5F5F5]"
              onClick={() => {
                setEditModalTag(activeTag)
                setActiveMenuId(null)
              }}
            >
              Edit
            </button>
            <button
              type="button"
              className="w-full px-3 py-2 text-left text-[13px] text-[#011D58] hover:bg-[#F5F5F5]"
              onClick={() => {
                setDeleteModalTag(activeTag)
                setActiveMenuId(null)
              }}
            >
              Delete
            </button>
          </div>,
          document.body,
        )}

      <AddCustomTagModal
        isOpen={showCreateTagModal}
        portfolioId={sharedPortfolioId}
        createOnly
        isSharedPortfolio={isSharedPortfolio}
        onClose={() => setShowCreateTagModal(false)}
        onSuccess={() => {
          void handleCreateSuccess()
        }}
      />

      <EditTagModal
        isOpen={!!editModalTag}
        tagId={editModalTag?.id ?? ''}
        tagName={editModalTag?.name ?? ''}
        sharedPortfolioId={sharedPortfolioId}
        onClose={() => setEditModalTag(null)}
        onSuccess={() => {
          void handleEditSuccess()
        }}
      />

      <DeleteTagModal
        isOpen={!!deleteModalTag}
        tagId={deleteModalTag?.id ?? ''}
        tagName={deleteModalTag?.name ?? ''}
        onClose={() => setDeleteModalTag(null)}
        onSuccess={() => handleDeleteSuccess(deleteModalTag?.id ?? '')}
      />
    </>
  )
}
